import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import CryptoJS from 'crypto-js';

const poolData = {
    UserPoolId: 'us-east-1_XHjfr9SSr',
    ClientId: '3ejdf6bfltupbk2i7onb95ea5q'
};

const userPool = new CognitoUserPool(poolData);

// Generate SECRET_HASH for Cognito
const generateSecretHash = (username: string): string => {
    const clientSecret = '4rdbu0pttt1fu516t7t668nk48npbhas8s4ggsd187mlv6110i0';
    const clientId = '3ejdf6bfltupbk2i7onb95ea5q';
    const message = username + clientId;
    return CryptoJS.HmacSHA256(message, clientSecret).toString(CryptoJS.enc.Base64);
};

export interface AuthUser {
    username: string;
    email?: string;
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

export class CognitoService {
    static getCurrentUser(): CognitoUser | null {
        return userPool.getCurrentUser();
    }

    static async signIn(username: string, password: string): Promise<AuthUser> {
        return new Promise((resolve, reject) => {
            const authenticationDetails = new AuthenticationDetails({
                Username: username,
                Password: password,
            });

            const cognitoUser = new CognitoUser({
                Username: username,
                Pool: userPool,
            });

            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (session: CognitoUserSession) => {
                    const accessToken = session.getAccessToken().getJwtToken();
                    const idToken = session.getIdToken().getJwtToken();
                    const refreshToken = session.getRefreshToken().getToken();

                    resolve({
                        username,
                        accessToken,
                        idToken,
                        refreshToken,
                    });
                },
                onFailure: (err) => {
                    reject(err);
                },
                newPasswordRequired: (_userAttributes, _requiredAttributes) => {
                    reject(new Error('New password required'));
                },
            });
        });
    }

    // Alternative method using AWS SDK directly with SECRET_HASH
    static async signInWithSecretHash(username: string, password: string): Promise<AuthUser> {
        const secretHash = generateSecretHash(username);

        try {
            // Use fetch to call AWS Cognito directly
            const response = await fetch('https://cognito-idp.us-east-1.amazonaws.com/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-amz-json-1.1',
                    'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
                },
                body: JSON.stringify({
                    AuthFlow: 'USER_PASSWORD_AUTH',
                    ClientId: '3ejdf6bfltupbk2i7onb95ea5q',
                    AuthParameters: {
                        USERNAME: username,
                        PASSWORD: password,
                        SECRET_HASH: secretHash
                    }
                })
            });

            const result = await response.json();

            if (result.AuthenticationResult) {
                return {
                    username,
                    accessToken: result.AuthenticationResult.AccessToken,
                    idToken: result.AuthenticationResult.IdToken,
                    refreshToken: result.AuthenticationResult.RefreshToken,
                };
            } else {
                throw new Error(result.message || 'Authentication failed');
            }
        } catch (error: any) {
            throw new Error(error.message || 'Authentication failed');
        }
    }

    // Redirect to Cognito Hosted UI
    static signInWithHostedUI(): void {
        const clientId = '3ejdf6bfltupbk2i7onb95ea5q';
        const domain = 'us-east-1xhjfr9ssr.auth.us-east-1.amazoncognito.com';

        // Use current origin as redirect URI (works for both localhost and production)
        const redirectUri = encodeURIComponent(window.location.origin);
        const responseType = 'code';
        const scope = 'email+openid+phone'; // Use + separator and match Cognito console scopes

        const cognitoUrl = `https://${domain}/login?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}`;

        window.location.href = cognitoUrl;
    }

    static async signOut(): Promise<void> {
        return new Promise((resolve) => {
            const cognitoUser = userPool.getCurrentUser();
            if (cognitoUser) {
                cognitoUser.signOut();
            }
            localStorage.removeItem('isAuthenticated');
            resolve();
        });
    }

    static async getCurrentSession(): Promise<CognitoUserSession | null> {
        return new Promise((resolve) => {
            const cognitoUser = userPool.getCurrentUser();
            if (cognitoUser) {
                cognitoUser.getSession((err: any, session: CognitoUserSession) => {
                    if (err) {
                        resolve(null);
                    } else {
                        resolve(session);
                    }
                });
            } else {
                resolve(null);
            }
        });
    }

    static isAuthenticated(): boolean {
        const cognitoUser = userPool.getCurrentUser();
        return cognitoUser !== null;
    }

    // Handle OAuth callback from Cognito Hosted UI
    static async handleAuthCallback(): Promise<AuthUser | null> {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (!code) {
            return null;
        }

        try {
            // Exchange authorization code for tokens
            const response = await fetch('https://us-east-1xhjfr9ssr.auth.us-east-1.amazoncognito.com/oauth2/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    grant_type: 'authorization_code',
                    client_id: '3ejdf6bfltupbk2i7onb95ea5q',
                    client_secret: '4rdbu0pttt1fu516t7t668nk48npbhas8s4ggsd187mlv6110i0',
                    code: code,
                    redirect_uri: window.location.origin
                })
            });

            const tokens = await response.json();

            if (tokens.access_token) {
                // Decode the ID token to get user info
                const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));

                return {
                    username: idTokenPayload['cognito:username'] || idTokenPayload.sub,
                    email: idTokenPayload.email,
                    accessToken: tokens.access_token,
                    idToken: tokens.id_token,
                    refreshToken: tokens.refresh_token,
                };
            } else {
                throw new Error('Failed to exchange code for tokens');
            }
        } catch (error) {
            console.error('Error handling auth callback:', error);
            throw error;
        }
    }
}